/**
 * Data engine for the Overview onchain-activity widget.
 *
 * Wraps the shared `ApiClient` `useQuery` against the ephemeral blockchain feed
 * and selects its mode from the connected-wallet state (POST connected EVM
 * addresses, else GET the default preview chains). It exposes canonical rows +
 * multichain graph elements (via the shared transpose), and reconciles the two
 * "new activity" strategies the product wants:
 *
 *  - **interval** — refetch the page + graph on a cadence (works today, no infra)
 *  - **live**     — a `<StreamingProvider>` feeds rows via `setStreamedRows`
 *                   (merged ahead of the page with `mergeTransactionRows`), while
 *                   the page/graph still refresh on a slower cadence to stay fresh
 *  - **off**      — fetch once; refresh only on manual retry
 *
 * No bespoke fetch/transport/merge code — all of it comes from the lower packages.
 */
import * as React from "react";
import { useAddressInventory } from "@authe/web3";
import { useQuery } from "@authe/ui-core/api";
import { mergeTransactionRows, type TxRow } from "@authe/tx-table";
import type { GraphElements } from "@authe/tx-graph";
import type { LiveMode } from "@authe/fe-commons/display";
import { feedRows, feedToGraph, type OnchainFeedResponse } from "./transpose";
import {
  ONCHAIN_FEED_PATH,
  ONCHAIN_REFRESH_MS,
  MAX_FEED_ROWS,
  PREVIEW_LIMIT,
  WALLET_LIMIT,
} from "./constants";

export interface UseOnchainActivityOptions {
  liveMode: LiveMode;
}

export interface OnchainActivityResult {
  rows: TxRow[];
  graph: GraphElements;
  hasWallet: boolean;
  /** Connected wallet addresses the feed is querying (for the live-feed scope). */
  addresses: string[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  /** Lift newest-first streamed rows from `<OnchainLiveFeed>` (live mode only). */
  setStreamedRows: (rows: TxRow[]) => void;
  /** Count of streamed rows merged onto the page (drives the "N new" pill). */
  streamedCount: number;
}

export function useOnchainActivity({ liveMode }: UseOnchainActivityOptions): OnchainActivityResult {
  const { entries, isConnected } = useAddressInventory();
  const addresses = React.useMemo(
    () => Array.from(new Set(entries.map((e) => e.normalized).filter(Boolean))),
    [entries],
  );
  const hasWallet = isConnected && addresses.length > 0;
  const addressKey = addresses.join(",");

  const query = useQuery<OnchainFeedResponse>(
    (client, signal) =>
      hasWallet
        ? client.post<OnchainFeedResponse>(
            ONCHAIN_FEED_PATH,
            { addresses, graph: true, limit: WALLET_LIMIT },
            { signal },
          )
        : client.get<OnchainFeedResponse>(
            `${ONCHAIN_FEED_PATH}?graph=true&limit=${PREVIEW_LIMIT}`,
            { signal },
          ),
    { deps: [hasWallet, addressKey] },
  );

  const baseRows = React.useMemo(() => feedRows(query.data), [query.data]);
  const graph = React.useMemo(() => feedToGraph(query.data), [query.data]);

  // Streamed rows lifted from <OnchainLiveFeed>. Reset when the query identity
  // changes (wallet connect/disconnect) or when leaving Live, so we never show
  // another context's rows or stale stream output.
  const [streamedRows, setStreamedRows] = React.useState<TxRow[]>([]);
  React.useEffect(() => {
    setStreamedRows([]);
  }, [addressKey, hasWallet]);
  React.useEffect(() => {
    if (liveMode !== "live") setStreamedRows([]);
  }, [liveMode]);

  const rows = React.useMemo(
    () => (streamedRows.length ? mergeTransactionRows(streamedRows, baseRows, MAX_FEED_ROWS) : baseRows),
    [streamedRows, baseRows],
  );

  // Auto-refresh: poll in interval mode; in live mode refresh the page/graph on a
  // slower cadence (the stream drives immediacy for the table between refetches).
  const refetch = query.refetch;
  React.useEffect(() => {
    if (liveMode === "off") return;
    const everyMs = liveMode === "live" ? ONCHAIN_REFRESH_MS * 4 : ONCHAIN_REFRESH_MS;
    const id = window.setInterval(() => refetch(), everyMs);
    return () => window.clearInterval(id);
  }, [liveMode, refetch]);

  return {
    rows,
    graph,
    hasWallet,
    addresses,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch,
    setStreamedRows,
    streamedCount: streamedRows.length,
  };
}
